// Tiny singleton toast bus. Used to surface "added to cart" feedback
// + soft errors that don't deserve a full inline alert.
//
// Why a module-level event bus instead of a React context: every
// component that triggers a toast (PanelBlock + cross-sell quick-add,
// ServicePanel Book button, GiftCardPurchasePanel, etc.) imports
// `pushToast(message)` directly without having to thread props or
// wrap providers. The renderer (apps/widget/src/components/Toast.tsx)
// subscribes once at the WidgetShell level.

export type ToastTone = "info" | "success" | "warn";

export interface ToastEvent {
  id: number;
  message: string;
  tone: ToastTone;
}

type Listener = (event: ToastEvent) => void;

const listeners = new Set<Listener>();
let nextId = 1;

/**
 * Subscribes a listener to future toast events. Returns an
 * unsubscribe function for cleanup on unmount.
 */
export function subscribeToToasts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Triggers a toast for every subscribed listener. No-ops if no
 * listener is mounted yet (i.e. before WidgetShell first renders);
 * harmless drop.
 */
export function pushToast(message: string, tone: ToastTone = "success"): void {
  const event: ToastEvent = { id: nextId++, message, tone };
  for (const listener of listeners) listener(event);
}
